/**
 * Privacy policy (08 §2, WS8-06).
 *
 * Written to be read, not to be defensible. Every claim here is something the code actually
 * does and something the tests actually check — the retention windows are the constants in
 * `app/workers/jobs_retention.py`, the providers are the ones in `.env`, and the delete button
 * at the bottom is the endpoint, not a mailto.
 */

const UPDATED = '20 August 2026'

export function Privacy() {
  return (
    <article className="mx-auto w-full max-w-[68ch] px-5 py-12">
      <p className="eyebrow">Privacy</p>
      <h1 className="mt-4 text-3xl">What we do with your information</h1>
      <p className="mt-3 text-sm text-muted">Last updated {UPDATED}</p>

      <Section title="Who we are">
        <p>
          ASB Home Renovations, a renovation contractor in Monmouth County, New Jersey. This policy
          covers the design studio at <strong>studio.asbhomerenovations.com</strong>. Questions go
          to{' '}
          <a href="mailto:studio@asbhomerenovations.com" className="text-blue-deep underline">
            studio@asbhomerenovations.com
          </a>
          .
        </p>
      </Section>

      <Section title="What we collect, and why">
        <ul>
          <li>
            <strong>Your name, email, phone and address.</strong> To arrange the visit and send you
            the estimate. The address is what tells us whether you are in the area Andrew covers.
          </li>
          <li>
            <strong>Photos of your rooms.</strong> To show you products in your own space. We strip
            the location data out of every photo before we store it — a phone photo routinely
            carries the coordinates of the house it was taken in, and we do not keep them.
          </li>
          <li>
            <strong>What you chose and what you asked.</strong> So Andrew arrives already knowing
            the job, and so we can tell whether the studio is useful.
          </li>
        </ul>
      </Section>

      <Section title="Who else sees it">
        <ul>
          <li>
            <strong>OpenRouter</strong>, and through it Google, when we generate a preview or answer
            a question. Your photo and your message are sent; your name, address and phone number
            are not.
          </li>
          <li>
            <strong>Cloudflare</strong>, which serves the site and blocks abuse.
          </li>
          <li>
            <strong>Google Workspace</strong>, which sends our email.
          </li>
          <li>
            <strong>Photon/OpenStreetMap</strong>, for address suggestions as you type.
          </li>
        </ul>
        <p>We do not sell anything to anyone, and we do not use your photos to train anything.</p>
      </Section>

      <Section title="How long we keep it">
        <ul>
          <li>
            <strong>Photos, selections and previews:</strong> 180 days after your design session
            ends, then deleted automatically.
          </li>
          <li>
            <strong>Conversations with the assistant:</strong> 12 months.
          </li>
          <li>
            <strong>Your estimate and the work we did:</strong> kept as a business record.
          </li>
        </ul>
      </Section>

      <Section title="Deleting everything">
        <p>
          Open your workspace and use <strong>Delete my data</strong>, or email us. It removes your
          details, your photos, your previews and your messages within a few minutes. It cannot be
          undone, and we keep one dated line saying a deletion happened — with nothing in it that
          identifies you.
        </p>
      </Section>

      <Section title="Previews are previews">
        <p>
          Every image the studio generates is labelled “AI preview — actual product &amp; finish may
          vary”. Prices shown are the store's on the date shown, and the labour figure is an
          estimate from Andrew's rates. Nothing in the studio is a quote; Andrew confirms everything
          on site.
        </p>
      </Section>
    </article>
  )
}

export function Terms() {
  return (
    <article className="mx-auto w-full max-w-[68ch] px-5 py-12">
      <p className="eyebrow">Terms</p>
      <h1 className="mt-4 text-3xl">Using the design studio</h1>
      <p className="mt-3 text-sm text-muted">Last updated {UPDATED}</p>

      <Section title="What this is">
        <p>
          A tool for planning work with ASB Home Renovations. You upload a photo, pick real
          products, and see what they would look like. It is free, and using it does not commit you
          to anything.
        </p>
      </Section>

      <Section title="Prices, and what they are not">
        <p>
          Material prices come from the store's own page on the date shown and change without
          notice. Labour figures are estimates from Andrew's rate table, shown as “estimated labor
          from”. <strong>Nothing in the studio is a quote or an offer.</strong> Andrew measures on
          site and sends the price after the visit.
        </p>
        <p>
          Quantities the studio works out from a photo are approximate by design, and are always
          marked as such. They exist to start a conversation, not to order materials.
        </p>
      </Section>

      <Section title="Previews">
        <p>
          Previews are generated by an image model. They show a real product in your room as closely
          as the software can manage, and they are not photographs of the finished work. Finishes,
          proportions and lighting will differ.
        </p>
      </Section>

      <Section title="Your photos">
        <p>
          They stay yours. You give us permission to use them to generate your previews and to show
          Andrew what you have in mind. We do not publish them, and we do not use them for anything
          else.
        </p>
      </Section>

      <Section title="Fair use">
        <p>
          Please do not upload photographs of other people, anything you do not have the right to
          share, or use the studio to abuse the service. We may end a session that does.
        </p>
      </Section>

      <Section title="The work itself">
        <p>
          Any renovation ASB carries out is covered by a separate written agreement signed after the
          visit. These terms cover only the studio.
        </p>
      </Section>
    </article>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-9">
      <h2 className="text-xl">{title}</h2>
      <div className="prose mt-3 grid gap-3 text-body [&_li]:py-0.5 [&_ul]:m-0 [&_ul]:grid [&_ul]:gap-2 [&_ul]:pl-5">
        {children}
      </div>
    </section>
  )
}
